import React from 'react';
import { CheckboxTableRowsProps, ContentLoader, formatNumber, MobileCardRowsProps } from '@dev/ee-components';
import { convertToLocalTime } from '@dev/ee-tools';
import { find, isEmpty, isNumber } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import useMobile from '@/services/hooks/useMobile';
import { StatusBadge } from '@/assets/StatusBadge';
import { contactStatusesTranslated } from '@/services/dictionaries/ContactStatuses';
import { getGeneralInfo } from '@/store/account/actions';
import { checkIfDataIsLoadedForContactsList } from '@/store/contacts/actions';
import { ApplicationState } from '@/store';

import { useHeadersOfContactsListTable } from './useHeadersOfContactsList';

export const useRowsOfContactsListTable = (): CheckboxTableRowsProps[] | MobileCardRowsProps[] => {
    const { t } = useTranslation();
    const { EnableContactFeatures } = useSelector(getGeneralInfo);
    const { listname, segmentname } = useParams<{ listname: string; segmentname: string }>();

    const { isDataLoaded, collection } = useSelector((store: ApplicationState) =>
        checkIfDataIsLoadedForContactsList(store, listname || segmentname)
    );
    const isMobile = useMobile();
    const headers = useHeadersOfContactsListTable();

    if (isEmpty(collection) && isDataLoaded) {
        return [];
    }

    if (isMobile) {
        if (!isDataLoaded) {
            return [...new Array(3)].map((el, index) =>
                (headers as string[])?.reduce(
                    (acc, header) => ({
                        ...acc,
                        [header]: <ContentLoader height={20} mode="row" key={`${index}`} />,
                    }),
                    { tableTitle: <ContentLoader height={20} mode="row" key={`table-title${index}`} /> } as any
                )
            );
        }

        return collection?.map((contact): any => {
            const { translationKey, enumValue } =
                find(contactStatusesTranslated, (status) => status.enumValue === contact.Status) || {};

            if (EnableContactFeatures) {
                return {
                    status: <StatusBadge value={translationKey} status={enumValue} />,
                    opened: formatNumber(+contact.TotalOpened || 0),
                    sent: formatNumber(+contact.TotalSent || 0),
                    clicked: formatNumber(+contact.TotalClicked || 0),
                    bounced: formatNumber(+contact.TotalFailed || 0),
                    tableTitle: contact.Email,
                    id: contact.Email,
                    returnOnItemClick: contact.Email,
                };
            } else {
                return {
                    status: <StatusBadge value={translationKey} status={enumValue} />,
                    tableTitle: contact.Email,
                    returnOnItemClick: contact.Email,
                };
            }
        });
    }

    return (
        collection?.map((contact): any => {
            const { translationKey, enumValue } =
                find(contactStatusesTranslated, (status) => status.enumValue === contact.Status) || {};
            if (EnableContactFeatures) {
                return {
                    addedDate: convertToLocalTime(contact.DateAdded, 'L'),
                    addedTime: convertToLocalTime(contact.DateAdded, 'LT'),
                    bounced: formatNumber(+contact.TotalFailed || 0),
                    clicked: formatNumber(+contact.TotalClicked || 0),
                    email: contact.Email,
                    error: false,
                    lastname: contact.LastName,
                    name: contact.FirstName,
                    opened: formatNumber(+contact.TotalOpened || 0),
                    sent: formatNumber(+contact.TotalSent || 0),
                    source: isNumber(contact.Source) ? '' : t(contact.Source?.toLowerCase()),
                    status: <StatusBadge status={enumValue} value={translationKey} />,
                    updatedDate: convertToLocalTime(contact.DateUpdated, 'L'),
                    updatedTime: convertToLocalTime(contact.DateUpdated, 'LT'),
                };
            } else {
                return {
                    addedDate: convertToLocalTime(contact.DateAdded, 'L'),
                    addedTime: convertToLocalTime(contact.DateAdded, 'LT'),
                    email: contact.Email,
                    error: false,
                    lastname: contact.LastName,
                    name: contact.FirstName,
                    source: '',
                    status: <StatusBadge status={enumValue} value={translationKey} />,
                    updatedDate: convertToLocalTime(contact.DateUpdated, 'L'),
                    updatedTime: convertToLocalTime(contact.DateUpdated, 'LT'),
                };
            }
        }) || []
    );
};
